// src/encounter/intelligence/rail-footer.tsx
//
// The Scrips Intelligence rail footer, pinned under the cards (round 18r design, frame `encounter-visit`):
//   row 1 — the "Explain a body system" chip (opens the body-system choice; Heart & vessels is the one system the
//           engine explains today, POST /api/intelligence/explain) and the lens menu ("Retests");
//   row 2 — the Ask field and its button;
//   row 3 — the two fixed lines.
// Only what has an engine route is operable. Ask sends the clinician's question to the engine's state interpreter
// (Samer, 4 Nov 2026; encounter owner, 1 Nov: the free Ask goes to the state interpreter); the answer is the Ask card's.
// The lens menu has no engine route, so it is drawn with the design's words and disabled. Nothing here logs.
import { useState } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';
import { ASK_WORDS } from './ask-words';
import { RailMenu } from './rail-menu';
import { WORDS } from './explanation';
import { R } from './rail-classes';
import type { ExplainSystem } from './use-explain';
import { EXPLAIN_SYSTEMS, RAIL_WORDS } from './rail-words';

export interface IntelligenceRailFooterProps {
  /** Runs Explain for the chosen system. Absent: the chip is disabled (no engine for this patient). */
  onExplain?: (system: ExplainSystem) => void;
  /** Sends a free question to the engine. Absent: Ask is disabled (no engine for this patient). */
  onAsk?: (question: string) => void;
}

export function IntelligenceRailFooter({ onExplain, onAsk }: IntelligenceRailFooterProps) {
  const [question, setQuestion] = useState('');
  const send = () => {
    const q = question.trim();
    if (!q || !onAsk) return;
    onAsk(q);
    setQuestion('');
  };
  return (
    <div data-slot="intelligence-footer" className="flex shrink-0 flex-col">
      <div className={R.chips}>
        {/* The body-system choice: the rail's DS menu (focus in, arrow keys, Escape / outside click close, focus back). */}
        <RailMenu
          label={RAIL_WORDS.explainChip}
          disabled={!onExplain}
          side="top"
          align="start"
          hostClassName={R.chipHost}
          menuClassName={R.menu}
          itemClassName={R.menuItem}
          items={EXPLAIN_SYSTEMS.map((s) => ({ key: s.id, label: s.label, onPick: () => onExplain?.(s.id) }))}
          trigger={
            <button type="button" data-slot="intelligence-explain-chip" className={R.chip}>
              {RAIL_WORDS.explainChip}
            </button>
          }
        />
        {/* No lens route on the engine: the design's lens, drawn and disabled. */}
        <button type="button" data-slot="intelligence-lens" className={R.lens} aria-label={RAIL_WORDS.lensAria} aria-haspopup="menu" disabled>
          {RAIL_WORDS.lens}
          <Icon name="down" size={12} aria-hidden />
        </button>
      </div>
      {/* Ask: Enter sends, Shift+Enter adds a line; the answer appears in the Ask card above. */}
      <form data-slot="intelligence-ask" className={R.askRow} onSubmit={(e) => { e.preventDefault(); send(); }}>
        <textarea
          className={R.askInput}
          aria-label={RAIL_WORDS.askAria}
          placeholder={RAIL_WORDS.ask}
          rows={1}
          maxLength={ASK_WORDS.maxLength}
          disabled={!onAsk}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
        />
        <button type="submit" className={R.askButton} disabled={!onAsk || !question.trim()}>
          <Icon name="send" size={16} aria-hidden />
          {RAIL_WORDS.ask}
        </button>
      </form>
      <div data-slot="intelligence-notes" className={R.notes}>
        <p className={R.note}>{RAIL_WORDS.draftLine}</p>
        <p className={R.note}>{WORDS.footer}</p>
      </div>
    </div>
  );
}
